import React, { useMemo, useState } from 'react';
import {
  Box,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  InputGroup,
  InputRightElement,
  Spinner,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import axios from 'axios';
import usePlacesService from 'react-google-autocomplete/lib/usePlacesAutocompleteService';
import { Path, PathValue, useFormContext } from 'react-hook-form';

import { AddressPinIcon } from 'clipsal-cortex-icons/src/custom-icons';
import { AUSTRALIAN_STATE_TO_TIMEZONE } from 'clipsal-cortex-utils/src/constants/timezone-states';

import { debounceEvent } from '../../../utils/component-helpers';
import { ManualAddressDialog, ManualAddressFormData } from './ManualAddressDialog';
import { AutocompletePrediction, PredictionsList } from './PlacePredictionList';
import { BaseAddressFields, SelectAddressProps } from './select-address-helpers';

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_KEY as string;

export function SelectAddress<TForm extends BaseAddressFields>({
  onUpdateSiteImageLink,
  testId = 'address',
  inputTitle = 'Address',
  containerProps,
  predictionContainerProps,
  formContainerProps,
  inputProps,
  fieldTypePrefix,
  addLatitudeLongitude = false,
  addTimezone = false,
}: SelectAddressProps) {
  const {
    setValue,
    reset,
    getValues,
    watch,
    formState: { errors },
  } = useFormContext<TForm>();
  const address = watch(fieldName('address')) as string;
  const [shouldDisplayAutocompleteOptions, setShouldDisplayAutocompleteOptions] = useState(false);
  const {
    isOpen: isManualAddressDialogOpen,
    onOpen: onOpenManualAddressDialog,
    onClose: onCloseManualAddressDialog,
  } = useDisclosure();
  const { placesService, placePredictions, getPlacePredictions, isPlacePredictionsLoading } = usePlacesService({
    apiKey: GOOGLE_MAPS_API_KEY,
    options: {
      input: '',
      types: [],
      componentRestrictions: { country: ['AU', 'US', 'NZ'] },
    },
  });

  // utility function to generate field names based on the prefix
  function fieldName<K extends keyof BaseAddressFields & string>(name: K): Path<TForm> & K {
    return (fieldTypePrefix ? `${fieldTypePrefix}.${name}` : name) as Path<TForm> & K;
  }

  async function handleSelectLocation(location: AutocompletePrediction) {
    placesService?.getDetails({ placeId: location.place_id }, async (details) => {
      const locationData = { city: '', state: '', country: '', postCode: '' };

      // iterate over address components to populate location data
      details?.address_components?.forEach((component) => {
        if (component.types.includes('locality')) {
          locationData.city = component?.long_name;
        } else if (component.types.includes('administrative_area_level_1')) {
          locationData.state = component?.short_name;
        } else if (component.types.includes('country')) {
          locationData.country = component?.long_name;
        } else if (component.types.includes('postal_code')) {
          locationData.postCode = component?.long_name;
        }
      });

      const latitude = details?.geometry?.location?.lat() ?? 0;
      const longitude = details?.geometry?.location?.lng() ?? 0;

      // Timezone is optional, so we set defaults if not provided
      let timezone = 'Australia/Sydney'; // default timezone
      if (addTimezone) {
        const {
          data: { timeZoneId },
        } = await axios.get(
          'https://maps.googleapis.com/maps/api/timezone/json?location=' +
            latitude +
            ',' +
            longitude +
            '&timestamp=' +
            Math.floor(Date.now() / 1000) +
            '&language=es&key=' +
            GOOGLE_MAPS_API_KEY
        );
        timezone = timeZoneId || 'Australia/Sydney';
      }

      const termsLength = location.terms.length;

      reset(
        {
          ...getValues(),
          address: location.description,
          city: locationData.city || location.terms[termsLength - 3]?.value,
          state: locationData.state || location.terms[termsLength - 2]?.value,
          country: locationData.country || location.terms[termsLength - 1]?.value,
          postCode: locationData.postCode,
          ...(addTimezone ? { timezone } : {}),
          ...(addLatitudeLongitude ? { latitude, longitude } : {}),
        },
        {
          keepDirty: true,
        }
      );

      setShouldDisplayAutocompleteOptions(false);

      if (onUpdateSiteImageLink)
        onUpdateSiteImageLink(
          'https://maps.googleapis.com/maps/api/staticmap' +
            '?sensor=false' +
            '&size=640x400' +
            '&maptype=satellite' +
            `&center=${latitude},${longitude}` +
            `&key=${GOOGLE_MAPS_API_KEY}` +
            '&zoom=20'
        );
    });
  }

  function handleSubmitManualAddress({ country, streetAddress, unit, city, state, postCode }: ManualAddressFormData) {
    // @TODO: when we move to US, look at timezones for US -- some states have multiple timezones
    const timezone =
      {
        Australia: AUSTRALIAN_STATE_TO_TIMEZONE[state],
        'New Zealand': 'Pacific/Auckland',
        'United States': 'America/Los_Angeles',
      }[country] ?? 'Australia/Sydney';

    reset(
      {
        ...getValues(),
        address: `${unit ? unit + '/' : ''}${streetAddress}, ${city} ${state}, ${country}`,
        city,
        state,
        country,
        postCode,
        timezone,
      },
      {
        keepDirty: true,
      }
    );

    onCloseManualAddressDialog();
    setShouldDisplayAutocompleteOptions(false);
  }

  const handlePlacePrediction = useMemo(
    () =>
      debounceEvent((searchTerm: string) => {
        getPlacePredictions({ input: searchTerm });
      }, 500),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return (
    <Box {...containerProps}>
      <FormControl isInvalid={!!errors?.address?.message} id="address" {...formContainerProps}>
        <FormLabel>{inputTitle}</FormLabel>
        <InputGroup>
          <Input
            value={address}
            onChange={(e) => {
              setValue(fieldName('address'), e.currentTarget.value as PathValue<TForm, Path<TForm>>, {
                shouldDirty: true,
              });
              setShouldDisplayAutocompleteOptions(true);
              handlePlacePrediction(e.currentTarget.value);
            }}
            placeholder={'12/22 George Street, Sydney, NSW'}
            type="text"
            data-private
            autoComplete="off"
            data-testid={testId}
            {...inputProps}
          />
          <InputRightElement>
            <AddressPinIcon color="customBlue.500" />
          </InputRightElement>
        </InputGroup>
        <FormErrorMessage>{errors?.address?.message as string | undefined}</FormErrorMessage>
      </FormControl>

      <Box
        onClick={onOpenManualAddressDialog}
        as="button"
        type="button"
        mt={1}
        fontSize="md"
        color="customBlue.500"
        data-testid="manual-address-button"
      >
        Enter address manually
      </Box>

      {/* Site address autocomplete */}
      <Box w={'100%'} data-private>
        {!isPlacePredictionsLoading ? (
          shouldDisplayAutocompleteOptions && (
            <PredictionsList {...{ predictionContainerProps, testId, placePredictions, handleSelectLocation }} />
          )
        ) : (
          <Flex direction={'column'} justify={'center'} align={'center'}>
            <Spinner size={'lg'} />
            <Text fontSize={'sm'}>Loading suggestions...</Text>
          </Flex>
        )}
      </Box>

      {isManualAddressDialogOpen && (
        <ManualAddressDialog
          currentValues={{
            address: getValues(fieldName('address')) as string,
            state: getValues(fieldName('state')) as string,
            country: getValues(fieldName('country')) as string,
            postCode: getValues(fieldName('postCode')) as string,
            city: getValues(fieldName('city')) as string,
          }}
          isOpen={isManualAddressDialogOpen}
          onClose={onCloseManualAddressDialog}
          onSubmitManualAddress={handleSubmitManualAddress}
        />
      )}
    </Box>
  );
}
